import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import axios from '../../config/axios';
import ApiService from '../../services/ApiService';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import {
  Container,
  PriceInputContainer,
  InputDiv,
  Label,
  Input,
  Select,
  PriceListContainer,
  PriceItemContainer,
  AddButton,
  DeleteButton
} from '../PriceManager/style';

export interface QuantityItem {
  quantity: number;
  unitOfMeasurementId: number;
  minQuantity?: number | null;
  maxQuantity?: number | null;
}

export interface UnitOfMeasurement {
  id: number;
  name: string;
}

export interface QuantityManagerRef {
  addPendingQuantity: () => boolean;
  savePendingEdit: () => boolean;
}

interface QuantityManagerProps {
  quantities: QuantityItem[];
  onChange: (quantities: QuantityItem[]) => void;
  baseUrl: string;
  label?: string;
  materialSpecs?: {
    format?: string;
    density?: string | number;
    dimension1?: string | number;
    dimension2?: string | number;
  };
  setWarningMessage?: (msg: string) => void;
  isFashion?: boolean;
  showMinMax?: boolean;
}

const QuantityManager = forwardRef<QuantityManagerRef, QuantityManagerProps>(({
  quantities,
  onChange,
  baseUrl,
  label = "Quantidades",
  materialSpecs,
  setWarningMessage,
  isFashion = false,
  showMinMax = false,
}, ref) => {
  const { getPreference, setPreference } = useUserPreferences();
  const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
  const [newQuantity, setNewQuantity] = useState<{ quantity: string; unitOfMeasurementId: string }>({
    quantity: '',
    unitOfMeasurementId: ''
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const parseOptionalNumber = (value: string): number | null => {
    if (!value) return null;
    const parsed = parseFloat(value.replace(',', '.'));
    return isNaN(parsed) ? null : parsed;
  };

  const updateExistingMinMax = (index: number, field: 'minQuantity' | 'maxQuantity', rawValue: string) => {
    const parsed = parseOptionalNumber(rawValue);
    const updated = quantities.map((q, i) => i === index ? { ...q, [field]: parsed } : q);
    onChange(updated);
  };

  const [isSeparateUnitsEnabled, setIsSeparateUnitsEnabled] = useState<boolean>(false);
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([]);

  const DEFAULT_UNITS: UnitOfMeasurement[] = [
    { id: 1, name: 'kg' },
    { id: 2, name: 'mm' },
    { id: 3, name: 'un' },
    { id: 4, name: 'm²' },
    { id: 5, name: 'm linear' },
    { id: 6, name: '%' },
    { id: 7, name: 'Litros' },
  ];

  useEffect(() => {
    const savedEnabled = getPreference<boolean>('stock_separate_units_enabled', false);
    const savedUnits = getPreference<number[]>('stock_separate_units_selected', []);
    setIsSeparateUnitsEnabled(savedEnabled);
    if (Array.isArray(savedUnits) && savedUnits.length > 0) {
      setSelectedUnitIds(savedUnits);
    }
  }, [getPreference]);

  useEffect(() => {
    const fetchUnits = async () => {
      let fetched: UnitOfMeasurement[] = [];
      try {
        const isServices = baseUrl.includes('/services');
        const response = isServices
          ? await ApiService.services.getUnits()
          : await ApiService.resources.getUnits();
        const unitsData = response.data || [];
        if (Array.isArray(unitsData) && unitsData.length > 0) {
          const allowedNames = ['kg', 'mm', 'un', 'unidade', 'm²', 'm linear', 'm2', 'm', 'linear', 'metro quadrado', 'metro linear', '%', 'porcentagem', 'litro', 'litros', 'l'];
          const filtered = unitsData.filter((unit: any) =>
            allowedNames.includes(String(unit.name || '').toLowerCase().trim())
          );
          fetched = filtered.length > 0 ? filtered : unitsData;
        }
      } catch (error) {
        console.error(error);
      }

      if (fetched.length === 0) {
        fetched = DEFAULT_UNITS;
      }
      setUnits(fetched);
    };

    fetchUnits();
  }, [baseUrl]);

  useEffect(() => {
    if (isSeparateUnitsEnabled && units.length > 0) {
      if (selectedUnitIds.length === 0) {
        const allIds = units.map(u => u.id);
        setSelectedUnitIds(allIds);
        setPreference('stock_separate_units_selected', allIds);
      } else {
        const existingIds = (quantities || []).map(q => q.unitOfMeasurementId);
        const missingIds = existingIds.filter(id => !selectedUnitIds.includes(id));
        if (missingIds.length > 0) {
          const merged = Array.from(new Set([...selectedUnitIds, ...missingIds]));
          setSelectedUnitIds(merged);
          setPreference('stock_separate_units_selected', merged);
        }
      }
    }
  }, [isSeparateUnitsEnabled, units, selectedUnitIds, quantities, setPreference]);

  const showAlert = (msg: string) => {
    if (!msg) return;
    if (setWarningMessage) {
      setWarningMessage(msg);
    } else {
      alert(msg);
    }
  };

  const calculateValue = (
    targetUnitName: string,
    otherQuantityValue: number
  ): number | null => {
    if (isFashion || !materialSpecs) return null;
    const { format, density, dimension1, dimension2 } = materialSpecs;

    const fmt = (format || '').toUpperCase().trim();
    const dens = Number(density);
    const d1 = Number(dimension1);
    const d2 = Number(dimension2) || 0;

    if (!fmt || !dens || !d1) {
      showAlert('Preencha a Dimensão 1 antes de calcular!');
      return null;
    }

    let area = 0;
    switch (fmt) {
      case 'BARRA':
      case 'REDONDO':
      case 'ANEL':
        if (fmt === 'ANEL' && d2 > 0) {
          area = Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2);
        } else {
          area = Math.PI * (d1 / 2) ** 2;
        }
        break;
      case 'QUADRADO':
      case 'CHAPA':
        if (!d2) {
          showAlert('Informe a dimensão 2 para o formato QUADRADO/CHAPA!');
          return null;
        }
        area = d1 * d2;
        break;
      case 'TUBO':
        if (!d2) {
          showAlert('Para TUBO informe as duas dimensões!');
          return null;
        }
        area = Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2);
        break;
      case 'LINGOTE':
        if (!d2) {
          showAlert('Informe a dimensão 2 para LINGOTE!');
          return null;
        }
        area = d1 * d2;
        break;
      default:
        showAlert('Formato de material inválido para cálculo!');
        return null;
    }

    if (targetUnitName === 'KG') {
      return (area * otherQuantityValue * dens) / 1_000_000;
    } else if (targetUnitName === 'MM') {
      return (otherQuantityValue * 1_000_000) / (area * dens);
    }

    return null;
  };

  const handleCalculateNewQuantity = () => {
    if (isFashion) return;
    const targetUnitId = Number(newQuantity.unitOfMeasurementId);
    const targetUnitName = getUnitName(targetUnitId).toUpperCase();

    const otherUnitName = targetUnitName === 'KG' ? 'MM' : 'KG';
    const otherUnit = units.find(u => u.name?.toUpperCase() === otherUnitName);
    const otherQtyItem = otherUnit ? quantities.find(q => q.unitOfMeasurementId === otherUnit.id) : null;

    if (!otherQtyItem) {
      showAlert(`Para calcular o ${targetUnitName === 'KG' ? 'Peso (KG)' : 'Comprimento (MM)'}, insira primeiro uma quantidade em ${otherUnitName} na lista!`);
      return;
    }

    const calculated = calculateValue(targetUnitName, otherQtyItem.quantity);
    if (calculated !== null) {
      setNewQuantity({
        ...newQuantity,
        quantity: String(calculated.toFixed(2)).replace('.', ',')
      });
    }
  };

  const handleCalculateEditingQuantity = (index: number, unitId: number) => {
    if (isFashion) return;
    const targetUnitName = getUnitName(unitId).toUpperCase();
    const otherUnitName = targetUnitName === 'KG' ? 'MM' : 'KG';
    const otherUnit = units.find(u => u.name?.toUpperCase() === otherUnitName);
    const otherQtyItem = otherUnit ? quantities.find(q => q.unitOfMeasurementId === otherUnit.id) : null;

    if (!otherQtyItem) {
      showAlert(`Para calcular o ${targetUnitName === 'KG' ? 'Peso (KG)' : 'Comprimento (MM)'}, insira primeiro uma quantidade em ${otherUnitName} na lista!`);
      return;
    }

    const calculated = calculateValue(targetUnitName, otherQtyItem.quantity);
    if (calculated !== null) {
      setEditingValue(String(calculated.toFixed(2)).replace('.', ','));
    }
  };

  const addQuantity = () => {
    if (newQuantity.quantity && newQuantity.unitOfMeasurementId) {
      const unitExists = quantities.some(q =>
        q.unitOfMeasurementId === parseInt(newQuantity.unitOfMeasurementId)
      );

      if (unitExists) {
        showAlert('Esta unidade de medida já foi adicionada. Escolha uma unidade diferente.');
        return false;
      }

      onChange([...quantities, {
        quantity: parseFloat(newQuantity.quantity.replace(',', '.')),
        unitOfMeasurementId: parseInt(newQuantity.unitOfMeasurementId),
        minQuantity: null,
        maxQuantity: null
      }]);
      setNewQuantity({ quantity: '', unitOfMeasurementId: '' });
      return true;
    }
    return false;
  };

  const addPendingQuantity = () => {
    if (newQuantity.quantity && newQuantity.unitOfMeasurementId) {
      const unitExists = quantities.some(q =>
        q.unitOfMeasurementId === parseInt(newQuantity.unitOfMeasurementId)
      );

      if (unitExists) {
        return false;
      }

      const newQtyItem = {
        quantity: parseFloat(newQuantity.quantity.replace(',', '.')),
        unitOfMeasurementId: parseInt(newQuantity.unitOfMeasurementId),
        minQuantity: null,
        maxQuantity: null
      };

      onChange([...quantities, newQtyItem]);
      setNewQuantity({ quantity: '', unitOfMeasurementId: '' });

      return true;
    }
    return false;
  };

  const savePendingEdit = () => {
    if (editingIndex !== null && editingValue) {
      const updatedQtyValue = parseFloat(editingValue.replace(',', '.')) || 0;
      const updatedQuantities = quantities.map((q, i) => i === editingIndex
        ? { ...q, quantity: updatedQtyValue }
        : q);
      onChange(updatedQuantities);
      setEditingIndex(null);
      setEditingValue('');
      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    addPendingQuantity,
    savePendingEdit
  }));

  const removeQuantity = (index: number) => {
    const updatedQuantities = quantities.filter((_, i) => i !== index);
    onChange(updatedQuantities);
  };

  const startEditing = (index: number, currentQty: number) => {
    setEditingIndex(index);
    setEditingValue(String(currentQty).replace('.', ','));
  };

  const saveEdit = (index: number) => {
    const updatedQtyValue = parseFloat(editingValue.replace(',', '.')) || 0;
    const updatedQuantities = quantities.map((q, i) => i === index
      ? { ...q, quantity: updatedQtyValue }
      : q);
    onChange(updatedQuantities);
    setEditingIndex(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleToggleSeparateUnits = async (enabled: boolean) => {
    setIsSeparateUnitsEnabled(enabled);
    await setPreference('stock_separate_units_enabled', enabled);
    if (enabled && selectedUnitIds.length === 0 && units.length > 0) {
      const defaultIds = units.map(u => u.id);
      setSelectedUnitIds(defaultIds);
      await setPreference('stock_separate_units_selected', defaultIds);
    }
  };

  const handleToggleUnitSelection = async (unitId: number) => {
    let nextSelected: number[];
    if (selectedUnitIds.includes(unitId)) {
      nextSelected = selectedUnitIds.filter(id => id !== unitId);
    } else {
      nextSelected = [...selectedUnitIds, unitId];
    }
    setSelectedUnitIds(nextSelected);
    await setPreference('stock_separate_units_selected', nextSelected);
  };

  const getUnitQuantityValue = (unitId: number): string => {
    const item = quantities.find(q => q.unitOfMeasurementId === unitId);
    if (!item || item.quantity === undefined || item.quantity === null) return '';
    return String(item.quantity).replace('.', ',');
  };

  const handleSeparateQuantityChange = (unitId: number, rawVal: string) => {
    const cleanedVal = rawVal.replace(/[^0-9,.]/g, '');
    const parsedNum = parseFloat(cleanedVal.replace(',', '.'));

    let nextQuantities: QuantityItem[];
    const existingIndex = quantities.findIndex(q => q.unitOfMeasurementId === unitId);

    if (cleanedVal.trim() === '' || isNaN(parsedNum)) {
      nextQuantities = quantities.filter(q => q.unitOfMeasurementId !== unitId);
    } else if (existingIndex >= 0) {
      nextQuantities = quantities.map((q, i) =>
        i === existingIndex ? { ...q, quantity: parsedNum } : q
      );
    } else {
      nextQuantities = [...quantities, { quantity: parsedNum, unitOfMeasurementId: unitId }];
    }

    onChange(nextQuantities);
  };

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleCalculateSeparateQuantity = (unitId: number) => {
    if (isFashion) return;
    const targetUnitName = getUnitName(unitId).toUpperCase();
    const otherUnitName = targetUnitName === 'KG' ? 'MM' : 'KG';
    const otherUnit = units.find(u => u.name?.toUpperCase() === otherUnitName);
    const otherQtyItem = otherUnit ? quantities.find(q => q.unitOfMeasurementId === otherUnit.id) : null;

    if (!otherQtyItem) {
      showAlert(`Para calcular o ${targetUnitName === 'KG' ? 'Peso (KG)' : 'Comprimento (MM)'}, insira primeiro uma quantidade em ${otherUnitName}!`);
      return;
    }

    const calculated = calculateValue(targetUnitName, otherQtyItem.quantity);
    if (calculated !== null) {
      const formatted = String(calculated.toFixed(2)).replace('.', ',');
      handleSeparateQuantityChange(unitId, formatted);
    }
  };

  const availableUnits = units.filter(unit =>
    !quantities.some(q => Number(q.unitOfMeasurementId) === Number(unit.id))
  );

  const getUnitName = (unitId: number | string) => {
    return units.find(unit => Number(unit.id) === Number(unitId))?.name || '';
  };

  const showCalcNew = !isFashion && newQuantity.unitOfMeasurementId && 
    (getUnitName(Number(newQuantity.unitOfMeasurementId)).toUpperCase() === 'KG' || 
     getUnitName(Number(newQuantity.unitOfMeasurementId)).toUpperCase() === 'MM');

  return (
    <Container style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <Label style={{ fontSize: '14px', fontWeight: 600, color: '#334155', margin: 0 }}>{label}</Label>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isSeparateUnitsEnabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              title="Escolher unidades para exibir"
              style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                padding: '3px 8px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                color: '#1d4ed8',
                fontSize: '12px',
                fontWeight: 600,
                transition: 'all 0.15s ease',
              }}
            >
              <Pencil size={13} color="#1d4ed8" />
              <span>Editar unidades</span>
            </button>
          )}

          <div
            onClick={() => handleToggleSeparateUnits(!isSeparateUnitsEnabled)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
          >
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>Unidades separadas</span>
            <div
              style={{
                width: '36px',
                height: '20px',
                backgroundColor: isSeparateUnitsEnabled ? '#2563eb' : '#cbd5e1',
                borderRadius: '10px',
                padding: '2px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  transform: isSeparateUnitsEnabled ? 'translateX(16px)' : 'translateX(0px)',
                  transition: 'transform 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {isSeparateUnitsEnabled ? (
        <div style={{ width: '100%' }}>
          {selectedUnitIds.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', padding: '12px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Nenhuma unidade selecionada.</span>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                style={{ color: '#2563eb', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                Clique para escolher as unidades.
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1vw', width: '100%' }}>
              {units
                .filter((u) => selectedUnitIds.includes(u.id))
                .map((unit) => {
                  const isCalcAllowed = !isFashion && (unit.name?.toUpperCase() === 'KG' || unit.name?.toUpperCase() === 'MM');
                  return (
                    <InputDiv key={unit.id} style={{ position: 'relative', width: '100%' }}>
                      <Label>Quantidade ({unit.name.toUpperCase()})</Label>
                      <Input
                        type="text"
                        value={getUnitQuantityValue(unit.id)}
                        onChange={(e) => handleSeparateQuantityChange(unit.id, e.target.value)}
                        placeholder="0,00"
                        style={{ width: '100%', paddingRight: isCalcAllowed ? '35px' : '8px' }}
                      />
                      {isCalcAllowed && (
                        <i
                          className="fa fa-calculator"
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '36px',
                            cursor: 'pointer',
                            color: '#007bff',
                            fontSize: '14px'
                          }}
                          onClick={() => handleCalculateSeparateQuantity(unit.id)}
                          title={`Calcular ${unit.name.toUpperCase()}`}
                        />
                      )}
                    </InputDiv>
                  );
                })}
            </div>
          )}
        </div>
      ) : (
        <>
          <PriceInputContainer>
            <InputDiv style={{ position: 'relative' }}>
              <Label>Quantidade</Label>
              <Input
                type="text"
                value={newQuantity.quantity}
                onChange={(e) => setNewQuantity({ ...newQuantity, quantity: e.target.value })}
                placeholder="0,00"
                style={{ paddingRight: showCalcNew ? '35px' : '8px' }}
              />
              {showCalcNew && (
                <i
                  className="fa fa-calculator"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '36px',
                    cursor: 'pointer',
                    color: '#007bff',
                    fontSize: '14px'
                  }}
                  onClick={handleCalculateNewQuantity}
                />
              )}
            </InputDiv>

            <InputDiv>
              <Label>Unidade</Label>
              <Select
                value={newQuantity.unitOfMeasurementId}
                onChange={(e) => setNewQuantity({ ...newQuantity, unitOfMeasurementId: e.target.value })}
              >
                <option value=""></option>
                {availableUnits.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name.toUpperCase()}
                  </option>
                ))}
              </Select>
            </InputDiv>
          </PriceInputContainer>

          <AddButton
            onClick={addQuantity}
            disabled={!newQuantity.quantity || !newQuantity.unitOfMeasurementId}
            style={{ marginTop: '0.5rem' }}
          >
            <Plus size={16} />
            {quantities.length === 0 ? `Adicionar ${label.toLowerCase()}` : 'Adicionar outra quantidade'}
          </AddButton>

          {quantities.length > 0 && (
            <PriceListContainer>
              {quantities.map((q, index) => {
                const showCalcEdit = !isFashion && (getUnitName(q.unitOfMeasurementId).toUpperCase() === 'KG' || 
                                     getUnitName(q.unitOfMeasurementId).toUpperCase() === 'MM');
                return (
                  <PriceItemContainer key={index}>
                    {editingIndex === index ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, position: 'relative' }}>
                        <Input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          style={{ flex: 1, padding: showCalcEdit ? '0 35px 0 8px' : '0 8px', height: '30px', borderRadius: '4px' }}
                        />
                        {showCalcEdit && (
                          <i
                            className="fa fa-calculator"
                            style={{
                              position: 'absolute',
                              right: '110px',
                              top: '8px',
                              cursor: 'pointer',
                              color: '#007bff',
                              fontSize: '13px'
                            }}
                            onClick={() => handleCalculateEditingQuantity(index, q.unitOfMeasurementId)}
                          />
                        )}
                        <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                          {getUnitName(q.unitOfMeasurementId).toUpperCase()}
                        </span>
                        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                          <button 
                            onClick={() => saveEdit(index)} 
                            disabled={!editingValue}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', display: 'flex', alignItems: 'center' }}
                          >
                            <Check size={16} />
                          </button>
                          <button 
                            onClick={cancelEdit}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, flexWrap: 'wrap' }}>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {q.quantity} {getUnitName(q.unitOfMeasurementId).toUpperCase()}
                          </span>
                          {showMinMax && (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <label style={{ fontSize: '12px', color: '#666' }}>Mín</label>
                                <Input
                                  type="number"
                                  value={q.minQuantity ?? ''}
                                  onChange={(e) => updateExistingMinMax(index, 'minQuantity', e.target.value)}
                                  placeholder="Opcional"
                                  style={{ width: '70px', padding: '0 6px', height: '30px', borderRadius: '4px' }}
                                />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <label style={{ fontSize: '12px', color: '#666' }}>Máx</label>
                                <Input
                                  type="number"
                                  value={q.maxQuantity ?? ''}
                                  onChange={(e) => updateExistingMinMax(index, 'maxQuantity', e.target.value)}
                                  placeholder="Opcional"
                                  style={{ width: '70px', padding: '0 6px', height: '30px', borderRadius: '4px' }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={() => startEditing(index, q.quantity)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007bff', display: 'flex', alignItems: 'center', padding: 0 }}
                          >
                            <Pencil size={12} />
                          </button>
                          <DeleteButton onClick={() => removeQuantity(index)} style={{ margin: 0 }}>
                            <Trash2 size={12} />
                          </DeleteButton>
                        </div>
                      </>
                    )}
                  </PriceItemContainer>
                );
              })}
            </PriceListContainer>
          )}
        </>
      )}

      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '420px',
              maxWidth: '90vw',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>
                Escolher Unidades Separadas
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>
              Selecione quais unidades de medida deseja preencher separadamente neste produto:
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
              {units.map((unit) => {
                const isSelected = selectedUnitIds.includes(unit.id);
                return (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => handleToggleUnitSelection(unit.id)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      border: isSelected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                      background: isSelected ? '#eff6ff' : '#ffffff',
                      color: isSelected ? '#1d4ed8' : '#475569',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {isSelected && <Check size={14} color="#1d4ed8" />}
                    {unit.name.toUpperCase()}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
});

QuantityManager.displayName = 'QuantityManager';

export default QuantityManager;
